import type {
  ApprovalRequest,
  ServerEvent,
  ModelCallSummary,
  SubagentTag,
} from '@ai-studio/shared';
import { create } from 'zustand';

import { api } from '../lib/api.js';
import { flushConsole } from '../lib/console-sync.js';
import { eventSocket } from '../lib/ws.js';
import { useBoard } from './board.js';
import { useTeam } from './team.js';
import { useWorkspace } from './workspace.js';
import { websiteChoice } from './website-model.js';

export type ActivityKind = 'status' | 'output' | 'message' | 'tool' | 'error';

export interface ActivityEntry {
  readonly id: number;
  /** 0 for the user. Otherwise the member who produced the line. */
  readonly memberId: number;
  /** Whose chat this line belongs to. */
  readonly chatMemberId: number;
  readonly kind: ActivityKind;
  readonly stream?: 'assistant' | 'thinking' | 'note';
  readonly text: string;
  readonly at: number;
  readonly ok?: boolean | null;
  readonly tool?: string;
  readonly callId?: string;
  readonly toolCallId?: string;
  readonly arguments?: Record<string, unknown>;
  readonly detail?: string | null;
}

/** One stored chat line, as the transcript route returns it. */
interface StoredChatLine {
  readonly id: number;
  readonly chatMemberId: number;
  readonly authorMemberId: number;
  readonly kind: ActivityKind;
  readonly stream: 'assistant' | 'thinking' | 'note' | null;
  readonly content: string;
  readonly tool: string | null;
  readonly ok: boolean | null;
  readonly callId: string | null;
  readonly toolCallId: string | null;
  readonly arguments: Record<string, unknown> | null;
  readonly detail: string | null;
  readonly at: string;
}

/** The worker tool that fans a request out to parallel sub-agents (Ultracode). */
export const SPAWN_AGENTS_TOOL = 'spawn_agents';

export type SubagentState = 'running' | 'done' | 'failed' | 'stopped';

/** One line of a sub-agent's own feed. Never part of the member's chat. */
export interface SubagentLine {
  readonly id: number;
  readonly kind: 'output' | 'message' | 'tool';
  readonly stream?: 'assistant' | 'thinking' | 'note';
  readonly text: string;
  readonly at: number;
  readonly ok?: boolean | null;
  readonly tool?: string;
  readonly callId?: string;
  readonly toolCallId?: string;
  readonly arguments?: Record<string, unknown>;
  readonly detail?: string | null;
}

export interface SubagentRun {
  readonly id: string;
  readonly label: string;
  readonly state: SubagentState;
  /** The agent's final text, once it ends. */
  readonly summary: string | null;
  readonly lines: readonly SubagentLine[];
  readonly startedAt: number;
  readonly endedAt: number | null;
}

/** The sub-agents one parent tool call started, in the order they first appeared. */
export interface SubagentGroup {
  readonly parentToolCallId: string;
  /** The model call that issued the parent tool call, when the worker reports it. */
  readonly parentCallId?: string;
  readonly memberId: number;
  readonly agents: readonly SubagentRun[];
  readonly updatedAt: number;
}

/**
 * Where a parent tool call's sub-agents are kept. Tool-call ids alone are not
 * unique: the Claude CLI transport reuses call_0_<tool> every round, and two
 * members can use the same ids, so the member and model call are part of it.
 */
export function subagentKey(memberId: number, parentCallId: string | undefined, parentToolCallId: string): string {
  return `${memberId}:${parentCallId ?? ''}:${parentToolCallId}`;
}

export interface MemberRuntime {
  readonly memberId: number;
  readonly state: 'idle' | 'starting' | 'running' | 'waiting_approval' | 'stopped' | 'error';
  readonly taskId: number | null;
  readonly detail: string | null;
}

interface AgentState {
  activity: ActivityEntry[];
  /** Member whose chat is open. Null follows the Assistant once the roster loads. */
  activeChatMemberId: number | null;
  approvals: ApprovalRequest[];
  runtime: Record<number, MemberRuntime>;
  calls: Record<string, ModelCallSummary>;
  /**
   * Live sub-agent activity keyed by the parent tool call that started it.
   *
   * Kept out of `activity`: parallel agents interleave their deltas, which the
   * chat feed would split into fragments, and their chatter would push the
   * member's own lines out of the visible window.
   */
  subagents: Record<string, SubagentGroup>;

  connect: () => () => void;
  /** Drop the feed and runtime map, e.g. when the project changes. */
  clearActivity: () => void;
  /** Open one member's chat. Null returns to the Assistant. */
  setActiveChat: (memberId: number | null) => void;
  /** Replace the feed with the project's stored chats. */
  loadTranscript: (projectId: number) => Promise<void>;
  decide: (id: string, decision: 'approved' | 'denied') => Promise<void>;
  sendToAssistant: (projectId: number, text: string) => Promise<void>;
  sendToMember: (projectId: number, memberId: number, text: string) => Promise<void>;
  stopMember: (memberId: number) => Promise<void>;
  runTask: (projectId: number, memberId: number, taskId: number) => Promise<void>;
}

let sequence = 0;
const nextId = () => ++sequence;

/**
 * Lines shown for one member.
 *
 * The server stores more than this and returns the same window. Keeping the
 * cap here too means a live stream cannot grow one chat past what a reload shows.
 */
export const CHAT_VISIBLE_LIMIT = 100;

/** Model-call metadata kept beside the feed. Not part of the visible transcript. */
const CALL_HISTORY = 400;

/** Lines kept per sub-agent. The parent tool's result keeps the full outcome. */
export const SUBAGENT_LINE_LIMIT = 80;

/** Parent tool calls whose sub-agents stay live. The least recently active go first. */
export const SUBAGENT_GROUP_LIMIT = 24;

/** A streamed sub-agent line keeps its tail past this length. */
const SUBAGENT_TEXT_LIMIT = 12_000;

/** Bumped on clear and on each load so a slow fetch cannot paint the wrong project. */
let transcriptGeneration = 0;

/** Keep the newest lines of each chat and preserve order. */
export function retainRecent(
  entries: readonly ActivityEntry[],
  limit = CHAT_VISIBLE_LIMIT,
): ActivityEntry[] {
  const kept: ActivityEntry[] = [];
  const counts = new Map<number, number>();
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const entry = entries[index]!;
    const count = counts.get(entry.chatMemberId) ?? 0;
    if (count >= limit) continue;
    counts.set(entry.chatMemberId, count + 1);
    kept.push(entry);
  }
  kept.reverse();
  return kept;
}

function assistantId(): number {
  return useTeam.getState().members.find((member) => member.role === 'assistant')?.id ?? 0;
}

/** Newest line in one chat, or -1 when that chat is empty. */
function lastChatIndex(entries: readonly ActivityEntry[], chatMemberId: number): number {
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    if (entries[index]?.chatMemberId === chatMemberId) return index;
  }
  return -1;
}

/**
 * The open streamed reply for one call.
 *
 * User lines are skipped, so a message sent while the agent is talking still
 * extends the same bubble. A tool or a different reply closes it.
 */
function openStreamIndex(
  entries: readonly ActivityEntry[],
  chatMemberId: number,
  stream: ActivityEntry['stream'],
  callId: string | undefined,
): number {
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const entry = entries[index];
    if (!entry || entry.chatMemberId !== chatMemberId || entry.memberId === 0) continue;
    if (entry.kind === 'output' && entry.stream === stream && entry.callId === callId) return index;
    return -1;
  }
  return -1;
}

function activeChatId(): number {
  return useAgents.getState().activeChatMemberId ?? assistantId();
}

function sameFinishedMessage(
  entry: ActivityEntry,
  memberId: number,
  callId: string | undefined,
  text: string,
): boolean {
  return (
    entry.kind === 'message' &&
    Boolean(callId) &&
    entry.callId === callId &&
    entry.chatMemberId === memberId &&
    entry.memberId === memberId &&
    entry.text === text
  );
}

function storedLine(row: StoredChatLine): ActivityEntry {
  const at = Date.parse(row.at);
  return {
    id: row.id,
    memberId: row.authorMemberId,
    chatMemberId: row.chatMemberId,
    kind: row.kind,
    ...(row.stream ? { stream: row.stream } : {}),
    text: row.authorMemberId === 0 && row.kind === 'message' ? `You: ${row.content}` : row.content,
    at: Number.isNaN(at) ? Date.now() : at,
    ok: row.ok,
    ...(row.tool ? { tool: row.tool } : {}),
    ...(row.callId ? { callId: row.callId } : {}),
    ...(row.toolCallId ? { toolCallId: row.toolCallId } : {}),
    ...(row.arguments ? { arguments: row.arguments } : {}),
    detail: row.detail,
  };
}

async function echoMessage(
  set: (updater: (state: AgentState) => Partial<AgentState>) => void,
  chatMemberId: number,
  text: string,
  post: () => Promise<unknown>,
): Promise<void> {
  // Echo before sending so a fast model call can associate with this message.
  const entry: ActivityEntry = {
    id: nextId(),
    memberId: 0,
    chatMemberId,
    kind: 'message',
    stream: 'assistant',
    text: `You: ${text}`,
    at: Date.now(),
  };
  set((state) => ({ activity: retainRecent([...state.activity, entry]) }));
  try {
    await post();
  } catch (error) {
    set((state) => ({ activity: state.activity.filter((item) => item.id !== entry.id) }));
    throw error;
  }
}

/** True when a live line is the same chat row a reload just fetched. */
function sameStoredLine(stored: ActivityEntry, live: ActivityEntry): boolean {
  if (stored.chatMemberId !== live.chatMemberId || stored.kind !== live.kind) return false;
  if (stored.kind === 'tool' && stored.toolCallId && stored.toolCallId === live.toolCallId) {
    if (stored.callId && live.callId && stored.callId !== live.callId) return false;
    return true;
  }
  if (
    stored.kind === 'message' &&
    stored.callId &&
    stored.callId === live.callId &&
    stored.text === live.text
  ) {
    return stored.memberId === live.memberId;
  }
  if (stored.memberId === 0 && live.memberId === 0 && stored.text === live.text) {
    return Math.abs(stored.at - live.at) < 15_000;
  }
  return false;
}

function clipTail(text: string): string {
  return text.length > SUBAGENT_TEXT_LIMIT ? `…${text.slice(-SUBAGENT_TEXT_LIMIT)}` : text;
}

function keepLines(lines: readonly SubagentLine[]): SubagentLine[] {
  return lines.length > SUBAGENT_LINE_LIMIT ? lines.slice(-SUBAGENT_LINE_LIMIT) : [...lines];
}

function pruneGroups(groups: Record<string, SubagentGroup>): Record<string, SubagentGroup> {
  const keys = Object.keys(groups);
  if (keys.length <= SUBAGENT_GROUP_LIMIT) return groups;
  const next = { ...groups };
  keys
    .sort((a, b) => groups[a]!.updatedAt - groups[b]!.updatedAt)
    .slice(0, keys.length - SUBAGENT_GROUP_LIMIT)
    .forEach((key) => delete next[key]);
  return next;
}

/**
 * Apply one change to one sub-agent, creating its group and row on first sight.
 *
 * Output can arrive before the `agent.subagent` event that announces the
 * agent, so whichever comes first creates the row from the tag's label.
 */
function withSubagent(
  groups: Record<string, SubagentGroup>,
  memberId: number,
  tag: SubagentTag,
  update: (run: SubagentRun) => SubagentRun,
): Record<string, SubagentGroup> {
  const now = Date.now();
  const key = subagentKey(memberId, tag.parentCallId, tag.parentToolCallId);
  const group = groups[key] ?? {
    parentToolCallId: tag.parentToolCallId,
    ...(tag.parentCallId !== undefined ? { parentCallId: tag.parentCallId } : {}),
    memberId,
    agents: [],
    updatedAt: now,
  };
  const index = group.agents.findIndex((run) => run.id === tag.id);
  const current: SubagentRun = group.agents[index] ?? {
    id: tag.id,
    label: tag.label || tag.id,
    state: 'running',
    summary: null,
    lines: [],
    startedAt: now,
    endedAt: null,
  };
  const next = update(current);
  if (index >= 0 && next === current) return groups;
  const agents =
    index >= 0 ? group.agents.map((run, i) => (i === index ? next : run)) : [...group.agents, next];
  return pruneGroups({ ...groups, [key]: { ...group, agents, updatedAt: now } });
}

/** Extend the agent's open stream for this call, or start a new line. */
function subagentDelta(
  run: SubagentRun,
  stream: SubagentLine['stream'],
  delta: string,
  callId: string | undefined,
): SubagentRun {
  // A finished message for this call means the delta is a replay.
  const finished = run.lines.some(
    (line) => line.kind === 'message' && line.stream === stream && line.callId === callId,
  );
  if (callId && finished) return run;
  const last = run.lines[run.lines.length - 1];
  if (last?.kind === 'output' && last.stream === stream && last.callId === callId) {
    return {
      ...run,
      lines: [...run.lines.slice(0, -1), { ...last, text: clipTail(last.text + delta) }],
    };
  }
  const line: SubagentLine = {
    id: nextId(),
    kind: 'output',
    stream,
    text: delta,
    at: Date.now(),
    ...(callId ? { callId } : {}),
  };
  return { ...run, lines: keepLines([...run.lines, line]) };
}

/**
 * The finished text for a call replaces its streamed line, which may already
 * sit behind the tool chips that call asked for.
 */
function subagentMessage(
  run: SubagentRun,
  stream: SubagentLine['stream'],
  text: string,
  callId: string | undefined,
): SubagentRun {
  const lines = run.lines;
  const same = (line: SubagentLine) => line.stream === stream && line.callId === callId;
  if (lines.some((line) => line.kind === 'message' && same(line) && line.text === text)) return run;
  const open = lines.findLastIndex((line) => line.kind === 'output' && same(line));
  if (open >= 0) {
    return {
      ...run,
      lines: lines.map((line, i) =>
        i === open ? { ...line, kind: 'message' as const, text: clipTail(text) } : line,
      ),
    };
  }
  const line: SubagentLine = {
    id: nextId(),
    kind: 'message',
    stream,
    text: clipTail(text),
    at: Date.now(),
    ...(callId ? { callId } : {}),
  };
  return { ...run, lines: keepLines([...lines, line]) };
}

/**
 * A tool start adds a pending chip; its end replaces it, matched by tool call
 * id within the same model call (some transports reuse ids across rounds).
 */
function subagentTool(
  run: SubagentRun,
  phase: 'start' | 'end',
  tool: Omit<SubagentLine, 'id' | 'at' | 'kind'>,
): SubagentRun {
  const index = tool.toolCallId
    ? run.lines.findLastIndex(
        (line) =>
          line.kind === 'tool' &&
          line.toolCallId === tool.toolCallId &&
          (!line.callId || !tool.callId || line.callId === tool.callId),
      )
    : -1;
  if (index >= 0) {
    if (phase === 'start' && run.lines[index]?.ok != null) return run;
    return {
      ...run,
      lines: run.lines.map((line, i) => (i === index ? { ...line, ...tool } : line)),
    };
  }
  const line: SubagentLine = { ...tool, id: nextId(), kind: 'tool', at: Date.now() };
  return { ...run, lines: keepLines([...run.lines, line]) };
}

/**
 * Each sub-agent's outcome from the spawn_agents result, by its number: the
 * report headers read "## 2. label (ok)" (or failed / stopped).
 */
export function spawnReports(
  detail: string | null | undefined,
): Map<number, { state: Exclude<SubagentState, 'running'>; summary: string }> {
  const reports = new Map<number, { state: Exclude<SubagentState, 'running'>; summary: string }>();
  if (!detail) return reports;
  const body = detail.replace(/\n+These are the sub-agents' own reports[\s\S]*$/, '');
  for (const part of body.split(/^## /m).slice(1)) {
    const newline = part.indexOf('\n');
    const header = newline >= 0 ? part.slice(0, newline) : part;
    const match = /^(\d+)\. .*\((ok|failed|stopped)\)\s*$/.exec(header);
    if (!match) continue;
    reports.set(Number(match[1]), {
      state: match[2] === 'ok' ? 'done' : (match[2] as 'failed' | 'stopped'),
      summary: newline >= 0 ? part.slice(newline + 1).trim() : '',
    });
  }
  return reports;
}

/**
 * Close sub-agents that can no longer report: their worker stopped, failed or
 * restarted, or the spawn that waits for them already returned. A returned
 * spawn's own result says how each agent ended (its state events are live
 * only, so a reconnect can miss them); anything else shows as stopped rather
 * than running forever.
 */
function settleSubagents(
  groups: Record<string, SubagentGroup>,
  matches: (group: SubagentGroup) => boolean,
  reports: ReadonlyMap<number, { state: Exclude<SubagentState, 'running'>; summary: string }> = new Map(),
): Record<string, SubagentGroup> {
  let changed = false;
  const now = Date.now();
  const next: Record<string, SubagentGroup> = {};
  for (const [key, group] of Object.entries(groups)) {
    if (!matches(group) || !group.agents.some((run) => run.state === 'running')) {
      next[key] = group;
      continue;
    }
    changed = true;
    next[key] = {
      ...group,
      agents: group.agents.map((run) => {
        if (run.state !== 'running') return run;
        // spawn_agents numbers its agents `<parent>#<n>`, as the report headers do.
        const number = /#(\d+)$/.exec(run.id)?.[1];
        const report = number ? reports.get(Number(number)) : undefined;
        return {
          ...run,
          state: report?.state ?? ('stopped' as const),
          summary: run.summary ?? (report?.summary || null),
          endedAt: now,
        };
      }),
    };
  }
  return changed ? next : groups;
}

export const useAgents = create<AgentState>((set, get) => ({
  activity: [],
  activeChatMemberId: null,
  approvals: [],
  runtime: {},
  calls: {},
  subagents: {},

  connect: () => {
    const push = (entry: Omit<ActivityEntry, 'id' | 'at'>) =>
      set((state) => ({
        activity: retainRecent([...state.activity, { ...entry, id: nextId(), at: Date.now() }]),
      }));

    const unsubscribe = eventSocket.subscribe((event: ServerEvent) => {
      switch (event.type) {
        case 'agent.status':
          // A stopped, failed or restarted worker took its sub-agents with it.
          if (event.state === 'starting' || event.state === 'stopped' || event.state === 'error') {
            set((state) => {
              const subagents = settleSubagents(
                state.subagents,
                (group) => group.memberId === event.memberId,
              );
              return subagents === state.subagents ? state : { subagents };
            });
          }
          set((state) => {
            return {
              runtime: {
                ...state.runtime,
                [event.memberId]: {
                  memberId: event.memberId,
                  state: event.state,
                  taskId: event.taskId,
                  detail: event.detail,
                },
              },
            };
          });
          if (event.state === 'error' && event.detail) {
            set((state) => {
              const lastIndex = lastChatIndex(state.activity, event.memberId);
              const last = lastIndex >= 0 ? state.activity[lastIndex] : undefined;
              if (
                last?.kind === 'error' &&
                last.memberId === event.memberId &&
                last.text === event.detail
              )
                return state;
              return {
                activity: retainRecent([
                  ...state.activity,
                  {
                    id: nextId(),
                    at: Date.now(),
                    memberId: event.memberId,
                    chatMemberId: event.memberId,
                    kind: 'error' as const,
                    text: event.detail!,
                  },
                ]),
              };
            });
          }
          break;

        case 'agent.output': {
          const tag = event.agent;
          if (tag) {
            // Coalesced per agent, so parallel agents never split each other's lines.
            set((state) => ({
              subagents: withSubagent(state.subagents, event.memberId, tag, (run) =>
                subagentDelta(run, event.stream, event.delta, event.callId),
              ),
            }));
            break;
          }
          // Coalesce streamed deltas onto the last entry from the same member
          // so the feed reads as paragraphs rather than thousands of fragments.
          // A finished message with this call id means the delta is a replay.
          set((state) => {
            if (
              event.callId &&
              state.activity.some(
                (entry) =>
                  entry.kind === 'message' &&
                  entry.callId === event.callId &&
                  entry.chatMemberId === event.memberId &&
                  entry.memberId === event.memberId,
              )
            ) {
              return state;
            }
            const index = openStreamIndex(
              state.activity,
              event.memberId,
              event.stream,
              event.callId,
            );
            const last = index >= 0 ? state.activity[index] : undefined;
            if (last) {
              const activity = [...state.activity];
              activity[index] = { ...last, text: last.text + event.delta };
              return { activity: retainRecent(activity) };
            }
            return {
              activity: retainRecent([
                ...state.activity,
                {
                  id: nextId(),
                  memberId: event.memberId,
                  chatMemberId: event.memberId,
                  kind: 'output' as const,
                  stream: event.stream,
                  text: event.delta,
                  at: Date.now(),
                  callId: event.callId,
                },
              ]),
            };
          });
          break;
        }

        case 'agent.message': {
          const tag = event.agent;
          if (tag) {
            set((state) => ({
              subagents: withSubagent(state.subagents, event.memberId, tag, (run) =>
                subagentMessage(run, event.stream, event.text, event.callId),
              ),
            }));
            break;
          }
          // A reply arrives twice: once as coalesced `output` deltas while it
          // streams, then as a final `message` with the whole text. Appending
          // the message as a new entry showed every reply twice, so the final
          // message replaces the streaming entry instead.
          set((state) => {
            const already = state.activity.some((entry) =>
              sameFinishedMessage(entry, event.memberId, event.callId, event.text),
            );
            if (already) {
              const activity = state.activity.filter(
                (entry) =>
                  !(
                    entry.kind === 'output' &&
                    entry.callId === event.callId &&
                    entry.chatMemberId === event.memberId
                  ),
              );
              return activity.length === state.activity.length ? state : { activity };
            }
            const index = openStreamIndex(
              state.activity,
              event.memberId,
              event.stream,
              event.callId,
            );
            const last = index >= 0 ? state.activity[index] : undefined;
            if (last) {
              const activity = [...state.activity];
              activity[index] = {
                ...last,
                kind: 'message' as const,
                text: event.text,
              };
              return { activity: retainRecent(activity) };
            }
            return {
              activity: retainRecent([
                ...state.activity,
                {
                  id: nextId(),
                  memberId: event.memberId,
                  chatMemberId: event.memberId,
                  kind: 'message' as const,
                  stream: event.stream,
                  text: event.text,
                  at: Date.now(),
                  callId: event.callId,
                },
              ]),
            };
          });
          break;
        }

        case 'agent.tool': {
          const tag = event.agent;
          if (tag) {
            const phase = event.phase;
            if (phase !== 'start' && phase !== 'end') break;
            set((state) => ({
              subagents: withSubagent(state.subagents, event.memberId, tag, (run) =>
                subagentTool(run, phase, {
                  text: `${event.tool}: ${event.summary}`,
                  tool: event.tool,
                  ok: event.ok,
                  ...(event.callId ? { callId: event.callId } : {}),
                  ...(event.toolCallId ? { toolCallId: event.toolCallId } : {}),
                  ...(event.arguments ? { arguments: event.arguments } : {}),
                  detail: event.detail,
                }),
              ),
            }));
            break;
          }
          // spawn_agents waits for its agents, so one still marked running missed its end.
          if (
            event.tool === SPAWN_AGENTS_TOOL &&
            event.phase === 'end' &&
            event.ok !== null &&
            event.toolCallId
          ) {
            const parent = event.toolCallId;
            const reports = spawnReports(event.detail);
            set((state) => {
              const subagents = settleSubagents(
                state.subagents,
                (group) =>
                  group.memberId === event.memberId &&
                  group.parentToolCallId === parent &&
                  (!group.parentCallId || !event.callId || group.parentCallId === event.callId),
                reports,
              );
              return subagents === state.subagents ? state : { subagents };
            });
          }
          if (event.phase === 'start' || event.phase === 'end') {
            const toolEntry = {
              memberId: event.memberId,
              chatMemberId: event.memberId,
              kind: 'tool' as const,
              text: `${event.tool}: ${event.summary}`,
              tool: event.tool,
              ok: event.ok,
              callId: event.callId,
              toolCallId: event.toolCallId,
              arguments: event.arguments,
              detail: event.detail,
            };
            set((state) => {
              let index = -1;
              if (event.toolCallId) {
                for (let i = state.activity.length - 1; i >= 0; i -= 1) {
                  const entry = state.activity[i];
                  if (
                    entry?.chatMemberId === event.memberId &&
                    entry.kind === 'tool' &&
                    entry.toolCallId === event.toolCallId
                  ) {
                    index = i;
                    break;
                  }
                }
              }
              if (index >= 0) {
                const current = state.activity[index];
                const sameCall =
                  !current?.callId || !event.callId || current.callId === event.callId;
                if (!sameCall) index = -1;
              }
              if (index >= 0) {
                if (event.phase === 'start' && state.activity[index]?.ok != null) return state;
                const activity = [...state.activity];
                activity[index] = { ...activity[index]!, ...toolEntry };
                return { activity };
              }
              return {
                activity: retainRecent([
                  ...state.activity,
                  { ...toolEntry, id: nextId(), at: Date.now() },
                ]),
              };
            });
          }
          break;
        }

        case 'agent.subagent': {
          const open = useWorkspace.getState().projectId;
          if (open !== null && event.projectId !== open) break;
          const tag: SubagentTag = {
            id: event.agentId,
            label: event.label,
            parentToolCallId: event.parentToolCallId,
            ...(event.parentCallId !== undefined ? { parentCallId: event.parentCallId } : {}),
          };
          set((state) => ({
            subagents: withSubagent(state.subagents, event.memberId, tag, (run) => ({
              ...run,
              label: event.label || run.label,
              state: event.state,
              summary: event.summary ?? run.summary,
              endedAt: event.state === 'running' ? null : (run.endedAt ?? Date.now()),
            })),
          }));
          break;
        }

        case 'agent.call':
          set((state) => {
            const calls = { ...state.calls, [event.call.id]: event.call };
            const ids = Object.keys(calls);
            for (const id of ids.slice(0, Math.max(0, ids.length - CALL_HISTORY))) delete calls[id];
            // A user message does not incur separate tokens; associate it with
            // the first model call that picks it up, not every later tool round.
            // A sub-agent's call never picks the message up.
            if (event.agent) return { calls };
            const user = [...state.activity]
              .reverse()
              .find(
                (entry) =>
                  entry.memberId === 0 &&
                  entry.chatMemberId === event.memberId &&
                  entry.kind === 'message' &&
                  !entry.callId,
              );
            const activity =
              user && event.call.status === 'running'
                ? state.activity.map((entry) =>
                    entry.id === user.id ? { ...entry, callId: event.call.id } : entry,
                  )
                : state.activity;
            return { calls, activity };
          });
          break;

        case 'approval.request':
          set((state) =>
            // The server republishes on every queue change; replace by id.
            state.approvals.some((a) => a.id === event.request.id)
              ? state
              : { approvals: [...state.approvals, event.request] },
          );
          break;

        case 'approval.resolved':
          set((state) => ({
            approvals: state.approvals.filter((a) => a.id !== event.requestId),
          }));
          break;

        case 'task.upsert':
          useBoard.getState().upsertLocal(event.task);
          break;

        case 'task.delete':
          break;

        case 'member.status':
          break;

        case 'error':
          push({
            memberId: 0,
            chatMemberId: activeChatId(),
            kind: 'error',
            text: event.message,
          });
          break;

        default:
          break;
      }
    });

    return unsubscribe;
  },

  decide: async (id, decision) => {
    // Remove immediately; the server confirms via approval.resolved.
    set((state) => ({ approvals: state.approvals.filter((a) => a.id !== id) }));
    await api.post(`/agents/approvals/${id}/decide`, { decision });
  },

  sendToAssistant: async (projectId, text) => {
    await flushConsole(projectId);
    await echoMessage(set, assistantId(), text, () =>
      api.post(`/agents/assistant/${projectId}/message`, { text, ...websiteChoice() }),
    );
  },

  sendToMember: async (projectId, memberId, text) => {
    const assistant = assistantId();
    if (assistant !== 0 && memberId === assistant) {
      await get().sendToAssistant(projectId, text);
      return;
    }
    await flushConsole(projectId);
    await echoMessage(set, memberId, text, () =>
      api.post(`/agents/members/${memberId}/message`, { text, ...websiteChoice() }),
    );
  },

  stopMember: async (memberId) => {
    const previous = get().runtime[memberId];
    set((state) => ({
      runtime: {
        ...state.runtime,
        [memberId]: {
          memberId,
          state: 'stopped',
          taskId: state.runtime[memberId]?.taskId ?? null,
          detail: 'stopped by user',
        },
      },
    }));
    try {
      await api.post(`/agents/workers/${memberId}/stop`);
    } catch (error) {
      if (previous) set((state) => ({ runtime: { ...state.runtime, [memberId]: previous } }));
      throw error;
    }
  },

  loadTranscript: async (projectId) => {
    const generation = ++transcriptGeneration;
    let response: { messages: StoredChatLine[] };
    try {
      response = await api.get<{ messages: StoredChatLine[] }>(
        `/agents/projects/${projectId}/transcript`,
      );
    } catch {
      // The live socket still appends. A failed read must not reject the effect.
      return;
    }
    if (generation !== transcriptGeneration) return;
    if (useWorkspace.getState().projectId !== projectId) return;
    const loaded = response.messages.map(storedLine);
    const highest = loaded.reduce((max, entry) => Math.max(max, entry.id), 0);
    if (highest > sequence) sequence = highest;
    set((state) => {
      const pool = [...loaded];
      const extra = state.activity.filter((entry) => {
        const index = pool.findIndex((row) => sameStoredLine(row, entry));
        if (index < 0) return true;
        pool.splice(index, 1);
        return false;
      });
      return { activity: retainRecent([...loaded, ...extra]) };
    });
  },

  setActiveChat: (memberId) => set({ activeChatMemberId: memberId }),

  runTask: async (projectId, memberId, taskId) => {
    await api.post('/agents/run-task', { projectId, memberId, taskId });
  },

  // Also clears the runtime map: a state left over from another project would
  // label this project's members with someone else's status.
  clearActivity: () => {
    transcriptGeneration += 1;
    set({ activity: [], activeChatMemberId: null, runtime: {}, calls: {}, subagents: {} });
  },
}));

/** Activity entries for one member, oldest first. */
export function activityFor(state: AgentState, memberId: number): ActivityEntry[] {
  return state.activity.filter((entry) => entry.memberId === memberId);
}

/**
 * The sub-agents one parent tool call started, or undefined when none were
 * seen. Workers that do not report the model call (Claude Code's own agents,
 * whose tool ids are unique) group without it.
 */
export function subagentsFor(
  state: Pick<AgentState, 'subagents'>,
  parent: { readonly memberId: number; readonly callId?: string | undefined; readonly toolCallId?: string | undefined },
): SubagentGroup | undefined {
  if (!parent.toolCallId) return undefined;
  return (
    (parent.callId ? state.subagents[subagentKey(parent.memberId, parent.callId, parent.toolCallId)] : undefined) ??
    state.subagents[subagentKey(parent.memberId, undefined, parent.toolCallId)]
  );
}

/** The lines the open chat shows for one member. */
export function visibleChat(
  entries: readonly ActivityEntry[],
  chatMemberId: number,
): ActivityEntry[] {
  return retainRecent(entries.filter((entry) => entry.chatMemberId === chatMemberId));
}
